import { articles, type Article } from "@/mocks/articles";

export type { Article, ArticleSection } from "@/mocks/articles";

export const PAGE_SIZE = 4;

export function getArticleById(id: string): Article | undefined {
  return articles.find((item) => item.id === id);
}

export function getArticlesByCategory(category: string): Article[] {
  return articles.filter((item) => item.category === category);
}

export function getSectionsText(article: Article): string {
  return article.sections
    .map((section) => `${section.heading} ${section.paragraphs.join(" ")}`)
    .join(" ");
}

export interface ArticleFilters {
  region?: string;
  year?: string;
  tag?: string;
  caseType?: string;
}

export function filterArticles(
  category: string,
  filters: ArticleFilters = {}
): Article[] {
  return getArticlesByCategory(category).filter((item) => {
    if (filters.region && item.region !== filters.region) return false;
    if (filters.year && String(item.year) !== filters.year) return false;
    if (filters.caseType && item.caseType !== filters.caseType) return false;
    if (filters.tag && !item.tags.includes(filters.tag)) return false;
    return true;
  });
}

export function sortArticles(
  list: Article[],
  sort: string,
  keyword = ""
): Article[] {
  const copy = [...list];
  if (sort === "oldest") {
    return copy.sort((a, b) => a.date.localeCompare(b.date));
  }
  if (sort === "relevance" && keyword.trim()) {
    const kw = keyword.trim().toLowerCase();
    return copy.sort(
      (a, b) => scoreArticle(b, kw) - scoreArticle(a, kw)
    );
  }
  return copy.sort((a, b) => b.date.localeCompare(a.date));
}

function scoreArticle(article: Article, keyword: string): number {
  let score = 0;
  const title = article.title.toLowerCase();
  const summary = article.summary.toLowerCase();
  const body = getSectionsText(article).toLowerCase();
  if (title.includes(keyword)) score += 6;
  if (article.tags.some((tag) => tag.toLowerCase().includes(keyword))) score += 4;
  if (summary.includes(keyword)) score += 2;
  if (body.includes(keyword)) score += 1;
  return score;
}

export function searchArticles(keyword: string, category = "all"): Article[] {
  const kw = keyword.trim().toLowerCase();
  const pool =
    category === "all"
      ? articles
      : articles.filter((item) => item.category === category);
  if (!kw) return [];
  return pool
    .filter(
      (article) =>
        article.title.toLowerCase().includes(kw) ||
        article.summary.toLowerCase().includes(kw) ||
        article.tags.some((tag) => tag.toLowerCase().includes(kw)) ||
        getSectionsText(article).toLowerCase().includes(kw)
    )
    .sort((a, b) => scoreArticle(b, kw) - scoreArticle(a, kw));
}

export function getRelated(article: Article, limit = 3): Article[] {
  const sameCategory = articles.filter(
    (item) => item.category === article.category && item.id !== article.id
  );
  const byTag = sameCategory.filter((item) =>
    item.tags.some((tag) => article.tags.includes(tag))
  );
  const rest = sameCategory.filter((item) => !byTag.includes(item));
  return [...byTag, ...rest].slice(0, limit);
}

export function uniqueValues(
  category: string,
  key: "region" | "year" | "caseType" | "tag"
): string[] {
  const list = getArticlesByCategory(category);
  const set = new Set<string>();
  list.forEach((item) => {
    if (key === "tag") {
      item.tags.forEach((tag) => set.add(tag));
    } else {
      const value = item[key];
      if (value) set.add(String(value));
    }
  });
  return Array.from(set).sort((a, b) => b.localeCompare(a, "zh-Hans-CN"));
}

export function formatDate(date: string): string {
  const [year, month, day] = date.split("-");
  return `${year}年${Number(month)}月${Number(day)}日`;
}

export function latestArticles(limit = 6): Article[] {
  return [...articles]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, limit);
}